// The recipe of the next shot as the local app shows it (frontend-orione/index.html): stop mode from brew.mode and the
// by_time/by_weight switches, the quick choice (brew.presets, format "presets"), ratios, the longest shot (FR-25), the
// lag in grams, bean profile helpers. Everything is turned into absolute set commands (AD-11): a whole recipe is one
// command, checked and applied together.
import { findCommand, orioneCommandCatalog, type CommandCatalog } from '@brewcore/protocol';
import type { ParamValue } from './store.js';

export type StopMode = 'hand' | 'time' | 'weight';
type Values = (id: string) => ParamValue | undefined;

/** "Stop: by hand | by time | by weight" from brew.mode plus the two switches (stopMode() of the local app). */
export function stopModeOf(v: Values, scale: boolean): StopMode {
  if (v('brew.mode') !== 1) return 'hand';
  if (scale && v('brew.by_weight.enabled') === true) return 'weight';
  return v('brew.by_time.enabled') === true ? 'time' : 'hand';
}

/** One set for a stop mode; by_weight only where the machine has a scale (catalog requires: scale). */
export function stopParams(mode: StopMode, scale: boolean): Record<string, ParamValue> {
  if (mode === 'hand') return { 'brew.mode': 0 };
  return {
    'brew.mode': 1,
    'brew.by_time.enabled': mode === 'time',
    ...(scale ? { 'brew.by_weight.enabled': mode === 'weight' } : {}),
  };
}

/** Longest shot (FR-25, fixed in the firmware): max(60 s, target + PI + 10 s), by weight 60 s. */
export function longestShot(v: Values, scale: boolean): number {
  const mode = stopModeOf(v, scale);
  if (mode === 'weight') return 60;
  const pi = v('brew.pre_infusion.enabled') === true;
  const extra = pi ? Number(v('brew.pre_infusion.time') ?? 0) + Number(v('brew.pre_infusion.pause') ?? 0) : 0;
  return Math.max(60, Number(v('brew.by_time.target_time') ?? 0) + extra + 10);
}

/** Grams before the target the pump stops at: lag seconds of flow (at most 6 g, BrewLag::kMaxLeadGrams). */
export function lagGrams(lag: number, flow: number | null): number {
  return Math.min(6, lag * (flow ?? 2));
}

export const ratioOf = (dose: number, target: number): number | null =>
  dose > 0 && target > 0 ? Math.round((target / dose) * 100) / 100 : null;

/** Target weight of a ratio, in the 0.5 g steps of the target. */
export const goalOf = (ratio: number, dose: number): number => Math.round(dose * ratio * 2) / 2;

// ---------------------------------------------------------------------------------------------- quick choice

export interface Preset {
  name: string;
  /** bean name, '' = keep */
  bean: string;
  /** g, 0 = keep */
  dose: number;
  /** '' = keep */
  grind: string;
  /** °C, 0 = keep */
  temp: number;
  /** target time (s) */
  s: number;
  /** ratio dose : cup */
  r: number;
  /** pre-infusion: 1 on, 0 off, -1 keep */
  pi: number;
}

export const DEFAULT_PRESETS: readonly [string, number, number][] = [
  ['Espresso', 25, 2],
  ['Doppio', 30, 2.5],
  ['Lungo', 45, 4.5],
];

const plain = (x: unknown): string =>
  String(x ?? '')
    .replace(/[|;]/g, ' ')
    .trim();

/** brew.presets -> three recipes ("name|bean|dose|grind|temp|s|r|pi"; the older "s,r" still reads). */
export function parsePresets(text: unknown, dose: number): Preset[] {
  const v = String(text ?? '').split(';');
  return DEFAULT_PRESETS.map(([name, s, r], i) => {
    const f = (v[i] ?? '').split('|');
    if (f.length >= 8)
      return {
        name: f[0] || name,
        bean: f[1] ?? '',
        dose: Number(f[2]) || 0,
        grind: f[3] ?? '',
        temp: Number(f[4]) || 0,
        s: Number(f[5]) > 0 ? Number(f[5]) : s,
        r: Number(f[6]) > 0 ? Number(f[6]) : r,
        pi: f[7] === '' || f[7] === undefined ? -1 : Number(f[7]),
      };
    const [ps, px] = (v[i] ?? '').split(',').map(Number);
    // presets in grams (over 10, before 09.10.2026) are read as ratios at the current dose
    const ratio = px !== undefined && px > 10 && dose > 0 ? Math.round((px / dose) * 100) / 100 : px;
    return {
      name,
      bean: '',
      dose: 0,
      grind: '',
      temp: 0,
      s: ps && ps > 0 ? ps : s,
      r: ratio && ratio > 0 ? ratio : r,
      pi: -1,
    };
  });
}

export function formatPresets(list: readonly Preset[]): string {
  return list
    .map((p) =>
      [
        plain(p.name),
        plain(p.bean),
        p.dose || '',
        plain(p.grind),
        p.temp || '',
        p.s,
        p.r,
        p.pi < 0 ? '' : p.pi,
      ].join('|'),
    )
    .join(';');
}

const sameBean = (a: unknown, b: unknown) =>
  String(a ?? '')
    .trim()
    .toLowerCase() ===
  String(b ?? '')
    .trim()
    .toLowerCase();

/** The quick choice the current settings match (presetActive() of the local app). */
export function presetActive(p: Preset, v: Values, scale: boolean): boolean {
  const near = (id: string, x: number, tol: number) => Math.abs(Number(v(id) ?? -1) - x) < tol;
  const dose = Number(v('brew.dose') ?? 0);
  const rest =
    (!p.bean || sameBean(p.bean, v('brew.beans'))) &&
    (!p.dose || near('brew.dose', p.dose, 0.05)) &&
    (!p.grind || plain(v('brew.grind')) === p.grind) &&
    (!p.temp || near('brew.setpoint', p.temp, 0.05)) &&
    (p.pi < 0 || (v('brew.pre_infusion.enabled') === true) === p.pi > 0);
  const mode = stopModeOf(v, scale);
  if (!rest) return false;
  if (mode === 'time') return near('brew.by_time.target_time', p.s, 0.01);
  if (mode === 'weight') return near('brew.by_weight.target_weight', goalOf(p.r, p.dose || dose), 0.26);
  return false;
}

/** "Manuell": stop by hand and no pre-infusion. */
export const manualActive = (v: Values, scale: boolean): boolean =>
  stopModeOf(v, scale) === 'hand' && v('brew.pre_infusion.enabled') !== true;

function clamp(catalog: CommandCatalog, id: string, value: number): number {
  const c = findCommand(catalog, id);
  return Math.min(c?.max ?? Infinity, Math.max(c?.min ?? -Infinity, value));
}

/**
 * The commands of a tap on a quick choice: first the bean (its profile comes with it), then one set with what the
 * recipe sets itself, the stop and both targets. The stop is the one the machine will have after the bean switch (the
 * profile's stopMode for a known bean); by hand becomes weight with a scale, else time. The stop goes into the same set,
 * so brew.mode == 1 holds for the targets after applying it (visibleIf).
 */
export function presetCommands(
  p: Preset,
  v: Values,
  scale: boolean,
  beans: readonly Bean[] = [],
  catalog: CommandCatalog = orioneCommandCatalog,
): Record<string, ParamValue>[] {
  const out: Record<string, ParamValue>[] = [];
  const switching = !!p.bean && !sameBean(p.bean, v('brew.beans'));
  if (switching) out.push({ 'brew.beans': p.bean });
  const known = switching ? beans.find((b) => sameBean(b.name, p.bean)) : undefined;
  let mode = known?.stopMode ?? stopModeOf(v, scale);
  if (mode === 'weight' && !scale) mode = 'time';
  if (mode === 'hand') mode = scale ? 'weight' : 'time';
  const set: Record<string, ParamValue> = {};
  if (p.dose > 0) set['brew.dose'] = clamp(catalog, 'brew.dose', p.dose);
  if (p.grind) set['brew.grind'] = p.grind;
  if (p.temp > 0) set['brew.setpoint'] = clamp(catalog, 'brew.setpoint', p.temp);
  if (p.pi >= 0) set['brew.pre_infusion.enabled'] = p.pi > 0;
  Object.assign(set, stopParams(mode, scale));
  set['brew.by_time.target_time'] = clamp(catalog, 'brew.by_time.target_time', p.s);
  const dose = p.dose > 0 ? p.dose : Number((known?.dose ?? v('brew.dose')) || 18);
  if (scale)
    set['brew.by_weight.target_weight'] = clamp(catalog, 'brew.by_weight.target_weight', goalOf(p.r, dose));
  out.push(set);
  return out;
}

/** "Aktuelle Einstellungen übernehmen": the recipe from how the machine is set now. */
export function presetFromValues(p: Preset, v: Values): Preset {
  const dose = Number(v('brew.dose') ?? 0);
  const target = Number(v('brew.by_weight.target_weight') ?? 0);
  return {
    ...p,
    bean: plain(v('brew.beans')),
    dose,
    grind: plain(v('brew.grind')),
    temp: Number(v('brew.setpoint') ?? 0),
    s: Number(v('brew.by_time.target_time') ?? p.s),
    r: ratioOf(dose, target) ?? p.r,
    pi: v('brew.pre_infusion.enabled') === true ? 1 : 0,
  };
}

/** A target changed in the brew tab: the quick choice that was chosen takes it along, as a ratio (local saveTarget()). */
export function presetsWithTarget(
  presetsText: unknown,
  v: Values,
  scale: boolean,
  id: 'brew.by_time.target_time' | 'brew.by_weight.target_weight',
  value: number,
): string | null {
  const dose = Number(v('brew.dose') ?? 0);
  const list = parsePresets(presetsText, dose);
  const i = list.findIndex((p) => presetActive(p, v, scale));
  if (i < 0) return null;
  const p = list[i] as Preset;
  const next =
    id === 'brew.by_weight.target_weight'
      ? { ...p, r: ratioOf(p.dose || dose, value) ?? p.r }
      : { ...p, s: value };
  if (next.r === p.r && next.s === p.s) return null;
  list[i] = next;
  return formatPresets(list);
}

/** What a recipe sets besides its targets, e.g. "Hayb Guji · 18 g · Mahlgrad 2.4 · 93 °C". */
export function presetSummary(
  p: Preset,
  labels: { grind: string; pi: string; on: string; off: string },
): string {
  return [
    p.bean,
    p.dose ? `${fmt(p.dose)} g` : '',
    p.grind ? `${labels.grind} ${p.grind}` : '',
    p.temp ? `${fmt(p.temp)} °C` : '',
    p.pi < 0 ? '' : `${labels.pi} ${p.pi ? labels.on : labels.off}`,
  ]
    .filter(Boolean)
    .join(' · ');
}

export const fmt = (v: number, lang: 'de' | 'en' = 'de'): string =>
  v.toLocaleString(lang === 'de' ? 'de-DE' : 'en-GB', {
    minimumFractionDigits: v % 1 ? 1 : 0,
    maximumFractionDigits: 2,
  });

// ---------------------------------------------------------------------------------------------- beans

export interface Bean {
  id: number;
  name: string;
  dose?: number | null;
  grind?: string;
  setpoint?: number | null;
  tempEnd?: number | null;
  stopMode?: StopMode;
  targetTime?: number | null;
  targetWeight?: number | null;
  lag?: number | null;
  preinfusion?: { on: boolean; burst: number; pause: number } | null;
  roastDay?: number | null;
}

/** state/beans (current ID and profiles, last used first). */
export function beansOf(data: Record<string, unknown> | undefined): {
  current: number | null;
  items: Bean[];
} {
  const items = Array.isArray(data?.items) ? (data.items as Bean[]) : [];
  return { current: typeof data?.current === 'number' ? data.current : null, items };
}
